/**
 * "Do you speak a language other than English at home?" (Sam, 25 Oct 2026).
 *
 * Asked once at set-up and kept on the Account tab. The answer is saved at
 * once; when the grown-up screens are written in that language, the grown-up
 * can switch them to it. The children's practice stays in English.
 */
import { useState } from 'react'
import { api } from '../api'
import { radius, theme } from '../theme'
import { Card } from './ui'
import {
  HOME_LANGUAGES, SCREEN_LANGUAGES, homeLanguage, rememberScreenLanguage, screenLanguage,
  screenLanguageFor, setScreenLanguage, t, type ScreenLanguage,
} from '../lib/i18n'

export function LanguageCard({ onChanged, bare = false }: { onChanged?: () => void; bare?: boolean }) {
  const [home, setHome] = useState<string | null>(homeLanguage())
  const [screens, setScreens] = useState<ScreenLanguage>(screenLanguage())
  const [problem, setProblem] = useState<string | null>(null)

  const save = async (nextHome: string | null, nextScreens: ScreenLanguage) => {
    setProblem(null)
    try {
      await api.setLanguage(nextHome, nextScreens)
      setHome(nextHome)
      setScreens(nextScreens)
      setScreenLanguage(nextScreens, nextHome)
      rememberScreenLanguage(nextScreens)
      onChanged?.()
    } catch {
      setProblem('That did not save. Try again in a moment.')
    }
  }

  const written = screenLanguageFor(home)
  const label = SCREEN_LANGUAGES.find((l) => l.code === written)?.label
  const choice = (on: boolean, text: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{
        fontFamily: 'inherit', fontSize: 15, fontWeight: on ? 800 : 600, cursor: 'pointer',
        color: on ? theme.blue : theme.text, background: on ? theme.blueFaint : theme.card,
        border: `1.5px solid ${on ? theme.blueTint : theme.border}`,
        borderRadius: radius.sm, padding: '9px 14px',
      }}
    >
      {text}
    </button>
  )

  const body = (
    <>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
        {t('Do you speak a language other than English at home?')}
      </div>
      <p style={{ margin: '0 0 12px', color: theme.muted, fontSize: 15, lineHeight: 1.5 }}>
        {t('It helps us show the grown-up screens in your language. The children’s practice stays in English, because English is what they are learning.')}
      </p>
      <select
        value={home ?? ''}
        onChange={(e) => {
          const next = e.target.value || null
          const fits = screenLanguageFor(next)
          // Choosing a language whose screens exist switches them; English only switches back.
          void save(next, fits ?? 'en')
        }}
        style={{
          fontFamily: 'inherit', fontSize: 16, padding: '10px 12px', width: '100%',
          border: `1.5px solid ${theme.border}`, borderRadius: radius.sm,
          background: theme.card, color: theme.text,
        }}
      >
        {HOME_LANGUAGES.map((l) => (
          <option key={l.code ?? 'en'} value={l.code ?? ''}>{l.label}</option>
        ))}
      </select>

      {written && label ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 14, color: theme.muted, marginBottom: 6 }}>
            {t('Show the grown-up screens in')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {choice(screens === written, label, () => void save(home, written))}
            {choice(screens === 'en', t('English'), () => void save(home, 'en'))}
          </div>
        </div>
      ) : home && home !== 'other' ? (
        <p style={{ margin: '10px 0 0', fontSize: 14, color: theme.faint }}>
          {t('Not yet available in this language; the screens stay in English for now.')}
        </p>
      ) : null}

      {problem ? (
        <p style={{ margin: '10px 0 0', fontSize: 14, color: theme.red }}>{t(problem)}</p>
      ) : null}
    </>
  )

  return bare ? body : <Card>{body}</Card>
}
